/**
 * #813 Phase B: Command Web Management Hosting, the web twin of the iPad screen.
 *
 * It reads and writes only the existing canonical RPCs the iPad uses:
 * get_management_role_placements / set_management_role_placement (#755 preference)
 * and get_management_role_relocations / request_management_role_relocation /
 * cancel_management_role_relocation (#787 authority fence). There is no client
 * placement store and no client-owned authority: every change is one RPC attempt
 * followed by a reread, so iPad and web always show the same canonical truth.
 * Saving a preference never moves a role; a move is a request until the
 * destination accepts the handoff.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export type PlacementMode = 'automatic' | 'preferred' | 'pinned';
export type FallbackPolicy = 'automatic' | 'ordered' | 'none';

export interface Placement {
  readonly mode: PlacementMode;
  readonly preferredNodeId: string | null;
  readonly fallbackPolicy: FallbackPolicy;
  readonly fallbackNodeIds: readonly string[];
  readonly isDefault: boolean;
}

/** Every optional is Unknown when null: the node did not report it. */
export interface PlacementHost {
  readonly nodeId: string;
  readonly name: string;
  readonly hostKind: string | null;
  readonly online: boolean;
  readonly foreground: boolean | null;
  readonly deskMode: boolean | null;
  readonly workerCapacityEnabled: boolean | null;
  readonly runtimeOfferings: number;
}

export interface PlacementActual {
  readonly nodeId: string | null;
  readonly runtimeKey: string | null;
  readonly modelKey: string | null;
  readonly effortKey: string | null;
  readonly reportedAt: string | null;
}

export interface PlacementDecision {
  readonly recommended: boolean;
  readonly nodeId: string | null;
  readonly basis: string | null;
  readonly reason: string | null;
  readonly wouldMove: boolean;
}

export interface PlacementEvaluation {
  readonly nodeId: string;
  readonly eligible: boolean;
  readonly reasons: readonly string[];
  readonly unknown: readonly string[];
  readonly current: boolean;
}

export interface PlacementRole {
  readonly role: string;
  readonly placement: Placement;
  /** Null is Unknown: no execution reported since the route was configured. */
  readonly actual: PlacementActual | null;
  readonly decision: PlacementDecision;
  readonly evaluations: readonly PlacementEvaluation[];
}

export interface PlacementSnapshot {
  readonly roles: readonly PlacementRole[];
  readonly hosts: readonly PlacementHost[];
}

export type AuthorityState = 'active' | 'handoff_preparing' | 'handoff_ready' | 'unavailable';

export interface RoleAuthority {
  readonly role: string;
  readonly nodeId: string;
  readonly authorityGeneration: number;
  readonly state: AuthorityState | null;
  readonly turnInProgress: boolean;
  readonly pendingHandoffId: string | null;
}

export type HandoffState = 'preparing' | 'ready' | 'accepted' | 'cancelled';

export interface RoleHandoff {
  readonly handoffId: string;
  readonly role: string;
  readonly state: HandoffState;
  readonly sourceNodeId: string | null;
  readonly destinationNodeId: string | null;
  readonly requestedAt: string | null;
  readonly cancelReason: string | null;
}

export interface RelocationSnapshot {
  readonly authorities: readonly RoleAuthority[];
  /** Newest first, as Brunel returns them. */
  readonly handoffs: readonly RoleHandoff[];
}

type Json = Record<string, unknown>;

const obj = (value: unknown): Json | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Json : null;
const str = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const bool = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null);
const int = (value: unknown): number | null => (typeof value === 'number' && Number.isInteger(value) ? value : null);
const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);

const MODES: readonly PlacementMode[] = ['automatic', 'preferred', 'pinned'];
const POLICIES: readonly FallbackPolicy[] = ['automatic', 'ordered', 'none'];
const AUTHORITY_STATES: readonly AuthorityState[] = ['active', 'handoff_preparing', 'handoff_ready', 'unavailable'];
const HANDOFF_STATES: readonly HandoffState[] = ['preparing', 'ready', 'accepted', 'cancelled'];

function readPlacement(value: unknown): Placement | null {
  const row = obj(value);
  const mode = str(row?.mode) as PlacementMode | null;
  const policy = str(row?.fallbackPolicy) as FallbackPolicy | null;
  if (row === null || mode === null || !MODES.includes(mode) || policy === null || !POLICIES.includes(policy)) return null;
  return {
    mode,
    preferredNodeId: str(row.preferredNodeId),
    fallbackPolicy: policy,
    fallbackNodeIds: strings(row.fallbackNodeIds),
    isDefault: bool(row.isDefault) ?? false,
  };
}

function readRole(value: unknown): PlacementRole | null {
  const row = obj(value);
  const role = str(row?.role);
  const placement = readPlacement(row?.placement);
  const decision = obj(row?.decision);
  if (row === null || role === null || placement === null || decision === null) return null;
  const actual = obj(row.actual);
  return {
    role,
    placement,
    actual: actual === null ? null : {
      nodeId: str(actual.nodeId), runtimeKey: str(actual.runtimeKey), modelKey: str(actual.modelKey),
      effortKey: str(actual.effortKey), reportedAt: str(actual.reportedAt),
    },
    decision: {
      recommended: decision.status === 'recommended',
      nodeId: str(decision.nodeId),
      basis: str(decision.basis),
      reason: str(decision.reason),
      wouldMove: bool(decision.wouldMove) ?? false,
    },
    evaluations: (Array.isArray(row.evaluations) ? row.evaluations : []).flatMap((entry) => {
      const e = obj(entry);
      const nodeId = str(e?.nodeId);
      if (e === null || nodeId === null) return [];
      return [{
        nodeId, eligible: bool(e.eligible) ?? false, reasons: strings(e.reasons),
        unknown: strings(e.unknown), current: bool(e.current) ?? false,
      }];
    }),
  };
}

function readHost(value: unknown): PlacementHost | null {
  const row = obj(value);
  const nodeId = str(row?.nodeId);
  const name = str(row?.name);
  if (row === null || nodeId === null || name === null) return null;
  return {
    nodeId, name,
    hostKind: str(row.hostKind),
    online: bool(row.online) ?? false,
    foreground: bool(row.foreground),
    deskMode: bool(row.deskMode),
    workerCapacityEnabled: bool(row.workerCapacityEnabled),
    runtimeOfferings: int(row.runtimeOfferings) ?? 0,
  };
}

export function readPlacementSnapshot(value: unknown): PlacementSnapshot | null {
  const row = obj(value);
  if (row === null || !Array.isArray(row.roles) || !Array.isArray(row.hosts)) return null;
  return {
    roles: row.roles.map(readRole).filter((r): r is PlacementRole => r !== null),
    hosts: row.hosts.map(readHost).filter((h): h is PlacementHost => h !== null),
  };
}

export function readRelocationSnapshot(value: unknown): RelocationSnapshot | null {
  const row = obj(value);
  if (row === null || !Array.isArray(row.authorities) || !Array.isArray(row.handoffs)) return null;
  // Each authority row is { role, authority, actual }; Actual is shown from the placement read.
  const authorities = row.authorities.flatMap((entry) => {
    const a = obj(obj(entry)?.authority);
    const role = str(a?.role);
    const nodeId = str(a?.nodeId);
    const generation = int(a?.authorityGeneration);
    if (a === null || role === null || nodeId === null || generation === null) return [];
    const state = str(a.state) as AuthorityState | null;
    return [{
      role, nodeId, authorityGeneration: generation,
      state: state !== null && AUTHORITY_STATES.includes(state) ? state : null,
      turnInProgress: bool(a.turnInProgress) ?? false,
      pendingHandoffId: str(a.pendingHandoffId),
    }];
  });
  const handoffs = row.handoffs.flatMap((entry) => {
    const h = obj(entry);
    const handoffId = str(h?.handoffId);
    const role = str(h?.role);
    const state = str(h?.state) as HandoffState | null;
    if (h === null || handoffId === null || role === null || state === null || !HANDOFF_STATES.includes(state)) return [];
    return [{
      handoffId, role, state,
      sourceNodeId: str(h.sourceNodeId), destinationNodeId: str(h.destinationNodeId),
      requestedAt: str(h.requestedAt), cancelReason: str(h.cancelReason),
    }];
  });
  return { authorities, handoffs };
}

export function authorityFor(relocation: RelocationSnapshot | null, role: string): RoleAuthority | null {
  return relocation?.authorities.find((a) => a.role === role) ?? null;
}

export function openHandoffFor(relocation: RelocationSnapshot | null, role: string): RoleHandoff | null {
  const pending = authorityFor(relocation, role)?.pendingHandoffId ?? null;
  if (pending === null) return null;
  return relocation?.handoffs.find((h) => h.handoffId === pending && (h.state === 'preparing' || h.state === 'ready')) ?? null;
}

export function latestHandoffFor(relocation: RelocationSnapshot | null, role: string): RoleHandoff | null {
  return relocation?.handoffs.find((h) => h.role === role) ?? null;
}

// ---------------------------------------------------------------------------
// Placement drafts: the shapes the database accepts, same rules as BrunelKit.

export function placementIsValid(p: Omit<Placement, 'isDefault'>): boolean {
  switch (p.mode) {
    case 'automatic':
      return p.preferredNodeId === null && p.fallbackPolicy === 'automatic' && p.fallbackNodeIds.length === 0;
    case 'pinned':
      return p.preferredNodeId !== null && p.fallbackPolicy === 'none' && p.fallbackNodeIds.length === 0;
    case 'preferred': {
      if (p.preferredNodeId === null) return false;
      const ordered = p.fallbackPolicy === 'ordered';
      return ordered === (p.fallbackNodeIds.length > 0)
        && p.fallbackNodeIds.length <= 8
        && new Set(p.fallbackNodeIds).size === p.fallbackNodeIds.length
        && !p.fallbackNodeIds.includes(p.preferredNodeId);
    }
  }
}

export type PlacementDraft = Omit<Placement, 'isDefault'>;

/** The draft reduced to the shape its mode allows. */
export function normalizePlacement(p: PlacementDraft): PlacementDraft {
  switch (p.mode) {
    case 'automatic':
      return { mode: 'automatic', preferredNodeId: null, fallbackPolicy: 'automatic', fallbackNodeIds: [] };
    case 'pinned':
      return { mode: 'pinned', preferredNodeId: p.preferredNodeId, fallbackPolicy: 'none', fallbackNodeIds: [] };
    case 'preferred': {
      const order = (p.fallbackPolicy === 'ordered' ? p.fallbackNodeIds : [])
        .filter((id, index, all) => id !== p.preferredNodeId && all.indexOf(id) === index)
        .slice(0, 8);
      const policy: FallbackPolicy = p.fallbackPolicy === 'ordered' && order.length === 0 ? 'none' : p.fallbackPolicy;
      return { mode: 'preferred', preferredNodeId: p.preferredNodeId, fallbackPolicy: policy, fallbackNodeIds: order };
    }
  }
}

export function samePlacement(a: PlacementDraft, b: PlacementDraft): boolean {
  return a.mode === b.mode && a.preferredNodeId === b.preferredNodeId && a.fallbackPolicy === b.fallbackPolicy
    && a.fallbackNodeIds.length === b.fallbackNodeIds.length && a.fallbackNodeIds.every((id, i) => id === b.fallbackNodeIds[i]);
}

export function setPlacementRequest(orgId: string, role: string, draft: PlacementDraft) {
  const p = normalizePlacement(draft);
  return {
    orgId, role, mode: p.mode, preferredNodeId: p.preferredNodeId,
    fallbackPolicy: p.fallbackPolicy, fallbackNodeIds: [...p.fallbackNodeIds],
  };
}

export function resetPlacementRequest(orgId: string, role: string) {
  return { orgId, role, reset: true as const };
}

// ---------------------------------------------------------------------------
// Moves.

/** Roles the #787 fence relocates (overseer, ux, core, machinist). */
export const RELOCATABLE_ROLES: readonly string[] = ['overseer', 'ux', 'core', 'machinist'];

/**
 * #630: roles that never hold an authority of their own. Their deterministic reports and
 * configured AIs run inside the Machinist's Supervisor pass, fenced by the Machinist
 * authority turn, so the canonical authority for them is the Machinist's.
 */
export const MACHINIST_PASS_ROLES: readonly string[] = ['watchdog', 'project_keeper', 'qa_reviewer'];

/** The role whose canonical authority fences this role's work. */
export function authorityRoleFor(role: string): string {
  return MACHINIST_PASS_ROLES.includes(role) ? 'machinist' : role;
}

/**
 * Roles that no longer run: Observer UX and the Supervisors. Brunel may still return a
 * placement for one. It is left out of the settings so it cannot be configured, and
 * whatever is stored for it stays stored.
 */
export const RETIRED_PLACEMENT_ROLES: readonly string[] = ['ux', 'machinist', 'watchdog', 'project_keeper', 'qa_reviewer'];

export type MoveReadiness =
  | { readonly kind: 'ready'; readonly targetNodeId: string }
  | { readonly kind: 'not_relocatable' }
  | { readonly kind: 'no_target' }
  | { readonly kind: 'authority_not_claimed' }
  | { readonly kind: 'relocation_in_progress'; readonly handoffId: string }
  | { readonly kind: 'already_here' }
  | { readonly kind: 'target_ineligible'; readonly reasons: readonly string[] }
  | { readonly kind: 'preference_first'; readonly recommendedNodeId: string | null }
  | { readonly kind: 'turn_in_progress' };

/**
 * Mirrors the canonical preconditions so the button is honest. Brunel only
 * moves a role to its current recommendation, never interrupts a turn and never
 * stacks a second handoff; it still decides and may refuse.
 */
export function moveReadiness(role: PlacementRole, authority: RoleAuthority | null, targetNodeId: string | null): MoveReadiness {
  if (!RELOCATABLE_ROLES.includes(role.role)) return { kind: 'not_relocatable' };
  if (targetNodeId === null) return { kind: 'no_target' };
  if (authority === null) return { kind: 'authority_not_claimed' };
  if (authority.pendingHandoffId !== null) return { kind: 'relocation_in_progress', handoffId: authority.pendingHandoffId };
  if (authority.nodeId === targetNodeId) return { kind: 'already_here' };
  const evaluation = role.evaluations.find((e) => e.nodeId === targetNodeId);
  if (evaluation !== undefined && !evaluation.eligible) return { kind: 'target_ineligible', reasons: evaluation.reasons };
  if (!role.decision.recommended || role.decision.nodeId !== targetNodeId) {
    return { kind: 'preference_first', recommendedNodeId: role.decision.recommended ? role.decision.nodeId : null };
  }
  if (authority.turnInProgress) return { kind: 'turn_in_progress' };
  return { kind: 'ready', targetNodeId };
}

export const OWNER_MOVE_REASON = 'Owner requested move from Command Web Management Hosting';
export const OWNER_CANCEL_REASON = 'Owner cancelled move from Command Web Management Hosting';
export const OWNER_REAUTHORIZE_REASON = 'Owner re-authorized current route from Command Web Management Hosting';

export function moveRequest(orgId: string, role: string, targetNodeId: string) {
  return { orgId, role, targetNodeId, reason: OWNER_MOVE_REASON };
}

export function cancelRequest(orgId: string, handoffId: string) {
  return { orgId, handoffId, reason: OWNER_CANCEL_REASON };
}

export function reauthorizeRequest(orgId: string, role: string, authorityGeneration: number) {
  return { orgId, role, authorityGeneration, reason: OWNER_REAUTHORIZE_REASON };
}

// ---------------------------------------------------------------------------
// Words, identical in meaning to the iPad's.

/** The one Reviewer is hosted under the management role key 'core'. */
export function roleTitle(role: string): string {
  const titles: Record<string, string> = {
    overseer: 'Overseer', ux: 'Observer UX', core: 'Reviewer', machinist: 'Machinist',
    watchdog: 'Watchdog', project_keeper: 'Project Keeper', qa_reviewer: 'QA Reviewer',
  };
  return titles[role] ?? role;
}

export const MODE_LABEL: Readonly<Record<PlacementMode, string>> = {
  automatic: 'Automatic', preferred: 'Preferred host', pinned: 'Pinned to one host',
};

export const FALLBACK_LABEL: Readonly<Record<FallbackPolicy, string>> = {
  automatic: 'Any eligible host', ordered: 'Listed hosts, in order', none: 'No fallback',
};

export function reasonLabel(code: string): string {
  const words: Record<string, string> = {
    offline: 'Offline',
    no_runtime: 'No AI runtime',
    capacity_memory: 'Holding back: memory',
    capacity_video_memory: 'Holding back: video memory',
    capacity_cpu: 'Holding back: processor',
    capacity_graphics: 'Holding back: graphics',
    preferred_host_not_found: 'Preferred host no longer exists',
    preferred_host_ineligible: 'Preferred host is not eligible and fallback is off',
    pinned_host_ineligible: 'Pinned host is not eligible',
    no_eligible_fallback: 'No eligible fallback host',
    no_eligible_host: 'No eligible host',
  };
  return words[code] ?? (code.startsWith('capacity_') ? 'Holding back' : code);
}

export function basisLabel(code: string | null): string {
  const words: Record<string, string> = {
    automatic: 'Automatic choice', preferred: 'Preferred host', pinned: 'Pinned host',
    fallback_ordered: 'Fallback from the list', fallback_automatic: 'Automatic fallback',
  };
  return (code !== null && words[code]) || 'Unknown';
}

export function authorityStateLabel(state: AuthorityState | null): string {
  switch (state) {
    case 'active': return 'Active';
    case 'handoff_preparing': return 'Relocating: source is preparing the handoff';
    case 'handoff_ready': return 'Relocating: waiting for the destination to accept';
    case 'unavailable': return 'Unavailable';
    case null: return 'Unknown';
  }
}

export function handoffLabel(handoff: RoleHandoff, hostName: (id: string | null) => string): string {
  const route = `${hostName(handoff.sourceNodeId)} → ${hostName(handoff.destinationNodeId)}`;
  switch (handoff.state) {
    case 'preparing': return `Move pending (${route}): source is preparing. Authority has not moved.`;
    case 'ready': return `Move pending (${route}): waiting for the destination to accept. Authority has not moved.`;
    case 'accepted': return `Moved (${route}): the destination accepted authority.`;
    case 'cancelled': {
      const why = handoff.cancelReason === 'source_cancelled' ? 'the source host cancelled' : handoff.cancelReason ?? 'no reason given';
      return `Move cancelled (${route}): ${why}.`;
    }
  }
}

export function readinessLabel(readiness: MoveReadiness, hostName: (id: string | null) => string): string {
  switch (readiness.kind) {
    case 'ready': return `Ready to move to ${hostName(readiness.targetNodeId)}.`;
    case 'not_relocatable': return 'Brunel does not relocate this role.';
    case 'no_target': return 'No target host.';
    case 'authority_not_claimed': return 'No host holds this role yet, so there is nothing to move.';
    case 'relocation_in_progress': return 'A move is already in progress.';
    case 'already_here': return 'Authority is already on this host.';
    case 'target_ineligible':
      return readiness.reasons.length === 0
        ? 'That host is not eligible for this role now.'
        : `That host is not eligible: ${readiness.reasons.map(reasonLabel).join(' · ')}.`;
    case 'preference_first':
      return readiness.recommendedNodeId === null
        ? 'Brunel has no eligible recommendation. Save a preference naming this host first.'
        : `Brunel recommends ${hostName(readiness.recommendedNodeId)}. Save a preference naming this host first; saving does not move the role.`;
    case 'turn_in_progress': return 'A turn is running. Brunel will not interrupt it; try again when it finishes.';
  }
}

const ROUTE_REFUSALS = new Set([
  'route_unavailable', 'route_not_configured', 'exact_route_offering_unavailable', 'exact_model_not_available',
  'model_not_configured_for_relocation', 'destination_route_not_node_hosted', 'destination_not_on_available_route',
]);

export function refusalLabel(code: string | null): string {
  if (code !== null && ROUTE_REFUSALS.has(code)) {
    return "The role's exact runtime/model is not available on that host; Brunel will not substitute another. Nothing moved.";
  }
  switch (code) {
    case 'turn_in_progress': return 'A turn is running. Brunel did not interrupt it; nothing moved.';
    case 'relocation_in_progress': return 'Another move is already in progress; nothing new was requested.';
    case 'target_not_recommended': return 'That host is no longer Brunel\'s recommendation; nothing moved.';
    case 'authority_not_claimed': return 'No host holds this role yet; nothing moved.';
    case 'not_authority':
    case 'stale_authority': return 'Authority changed while you were looking; it has been reread.';
    case 'handoff_not_open': return 'That move already finished or was cancelled.';
    case 'forbidden': return 'Owner verification is required. Verify owner and try again.';
    case 'unauthorized': return 'Sign in again to change management hosting.';
    case 'not_found': return 'Brunel no longer has that move.';
    case null: return 'Brunel refused (unknown).';
    default: return `Brunel refused (${reasonLabel(code)}).`;
  }
}

/** Foreground / Desk Mode eligibility of an iPad management host; null otherwise. */
export function ipadEligibilityLabel(host: PlacementHost): string | null {
  if (host.hostKind !== 'ipad') return null;
  if (!host.online) return 'Not eligible: Brunel is not in the foreground on this iPad (locked, backgrounded, or not reporting).';
  if (host.deskMode === true) return 'Eligible while Brunel stays in the foreground · Desk Mode on.';
  return 'Eligible only while Brunel stays in the foreground. Desk Mode is off, so auto-lock will make it ineligible.';
}

export function actualLabel(actual: PlacementActual | null, hostName: (id: string | null) => string): string {
  if (actual === null || actual.nodeId === null) return 'Unknown';
  const runtime = [actual.runtimeKey, actual.modelKey, actual.effortKey].filter((v): v is string => v !== null).join(' · ');
  return runtime === '' ? hostName(actual.nodeId) : `${hostName(actual.nodeId)} · ${runtime}`;
}

// ---------------------------------------------------------------------------
// Canonical calls: one attempt each; a lost response is "unknown" and the
// caller rereads rather than replaying.

type Rpc = Pick<SupabaseClient, 'rpc'>;

export interface HostingRead {
  readonly placements: PlacementSnapshot | null;
  readonly relocation: RelocationSnapshot | null;
  readonly error: string | null;
  readonly relocationError: string | null;
}

async function readRpc(client: Rpc, name: string, orgId: string): Promise<{ value: unknown; code: string | null }> {
  try {
    const response = await client.rpc(name, { request: { orgId } });
    if (response.error) return { value: null, code: 'unreachable' };
    const result = obj(response.data);
    if (result?.ok === true) return { value: result.value, code: null };
    return { value: null, code: str(obj(result?.error)?.code) ?? 'internal' };
  } catch {
    return { value: null, code: 'unreachable' };
  }
}

export async function loadManagementHosting(client: Rpc, orgId: string): Promise<HostingRead> {
  const [placementRead, relocationRead] = await Promise.all([
    readRpc(client, 'get_management_role_placements', orgId),
    readRpc(client, 'get_management_role_relocations', orgId),
  ]);
  const placements = placementRead.code === null ? readPlacementSnapshot(placementRead.value) : null;
  const relocation = relocationRead.code === null ? readRelocationSnapshot(relocationRead.value) : null;
  return {
    placements,
    relocation,
    error: placements !== null ? null
      : placementRead.code === 'unreachable' ? 'Could not reach Brunel for management hosting.'
      : placementRead.code !== null ? `Brunel could not read management hosting (${placementRead.code}).`
      : 'Management hosting is not available on this Brunel yet.',
    relocationError: relocation !== null ? null
      : `Authority and move status are unavailable (${relocationRead.code ?? 'not on this Brunel yet'}). Moves are off until Brunel returns them.`,
  };
}

export type WriteOutcome =
  | { readonly outcome: 'ok'; readonly value: unknown }
  | { readonly outcome: 'rejected'; readonly code: string }
  | { readonly outcome: 'unknown' };

export async function writeOnce(client: Rpc, name: string, request: object): Promise<WriteOutcome> {
  try {
    const response = await client.rpc(name, { request });
    if (response.error) return { outcome: 'unknown' };
    const result = obj(response.data);
    if (result?.ok === true) return { outcome: 'ok', value: result.value };
    return { outcome: 'rejected', code: str(obj(result?.error)?.code) ?? 'internal' };
  } catch {
    return { outcome: 'unknown' };
  }
}

/** Words for a move request's answer. Never claims authority moved. */
export function moveOutcomeLabel(outcome: WriteOutcome, targetName: string): string {
  if (outcome.outcome === 'unknown') {
    return 'Brunel could not confirm the move request. Status has been reread; it will not be retried automatically.';
  }
  if (outcome.outcome === 'rejected') return `Move refused: ${refusalLabel(outcome.code)}`;
  const kind = str(obj(outcome.value)?.outcome);
  if (kind === 'preparing') return `Move requested to ${targetName}. Pending: authority moves only when the destination accepts.`;
  if (kind === 'already_current') return `${targetName} already holds this role. Nothing moved.`;
  return 'Brunel accepted the request but its answer was not understood. Status has been reread.';
}

export function reauthorizeOutcomeLabel(outcome: WriteOutcome): string {
  if (outcome.outcome === 'unknown') {
    return 'Brunel could not confirm re-authorization. Authority has been reread; it will not be retried automatically.';
  }
  if (outcome.outcome === 'rejected') return `Re-authorization refused: ${refusalLabel(outcome.code)}`;
  const kind = str(obj(outcome.value)?.outcome);
  if (kind === 'reauthorized') return 'Authority now accepts the exact current route on this host.';
  if (kind === 'already_current') return 'Authority already accepts the exact current route. Nothing changed.';
  return 'Brunel accepted re-authorization but its answer was not understood. Authority has been reread.';
}
